import { describeMutationError } from "@api/mutationErrors";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useSessionKey } from "@auth/sessionStore";
import { useCaptureNodesQuery } from "@capture/captureQueries";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import {
  Button,
  FieldError,
  Input,
  PanelError,
  SegmentedControl,
  Skeleton,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import {
  type DocEdit,
  displaysById,
  type MapDocument,
  type MapSegment,
  type MapSpace,
} from "./clinicMapClient";
import { useClinicMap, useSaveClinicMap } from "./clinicMapQueries";
import { DoorAreasDialog } from "./DoorAreasDialog";
import { MapCanvas, type MapSelection, type MapTool } from "./MapCanvas";
import {
  doorAt,
  GRID_MAX,
  withoutDoor,
  withoutStaleOutside,
} from "./mapGeometry";
import { MapInspector } from "./MapInspector";
import { type RoomsById, warningText } from "./mapWords";
import { RoomAreasDialog } from "./RoomAreasDialog";

/** brian refuses these in request validation, whose 422 has no sentence to show; its own map rules say what is wrong in words. */
const problemOf = (doc: MapDocument): string | null => {
  if (doc.spaces.some((s) => !s.room_uuid && !s.area))
    return "Tie every box to a room, or make it an area.";
  if (doc.spaces.some((s) => s.area && !s.label?.trim()))
    return "Give every area a name.";
  return null;
};

const TOOLS: { value: MapTool; label: string }[] = [
  { value: "select", label: "Select" },
  { value: "space", label: "Draw a box" },
  { value: "door", label: "Add a door" },
];

export const ClinicMapPanel = () => {
  const sessionKey = useSessionKey();
  const map = useClinicMap(sessionKey);
  const rooms = useRoomsListQuery(sessionKey, undefined, true);
  const nodes = useCaptureNodesQuery(sessionKey);
  const save = useSaveClinicMap(sessionKey);

  // The revision a draft started from: a refetch must not move it, or a save
  // would overwrite someone else's map without the 409.
  const [draft, setDraft] = useState<{
    doc: MapDocument;
    revision: number;
  } | null>(null);
  const [tool, setTool] = useState<MapTool>("select");
  const [selection, setSelection] = useState<MapSelection>(null);
  const [addBoxTo, setAddBoxTo] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [areasRoomId, setAreasRoomId] = useState<string | null>(null);
  const [doorsOf, setDoorsOf] = useState<string | null>(null);

  if (map.isError || rooms.isError)
    return (
      <PanelError
        label="the clinic map"
        onRetry={() => void Promise.all([map.refetch(), rooms.refetch()])}
      />
    );
  if (!map.data || !rooms.data) return <Skeleton className="h-96 w-full" />;

  const saved = map.data;
  const doc = draft?.doc ?? saved.document;
  const roomsById: RoomsById = new Map(rooms.data.map((r) => [r.id, r]));
  const displays = displaysById(saved.displays);
  const edit: DocEdit = (next) => {
    setDraft((current) => {
      const base = current ?? { doc: saved.document, revision: saved.revision };
      return {
        doc: typeof next === "function" ? next(base.doc) : next,
        revision: base.revision,
      };
    });
    setNotice(null);
  };
  const addDoor = (cell: MapSegment) => {
    const taken = doorAt(doc, cell);
    if (taken) {
      setSelection({ kind: "door", uuid: taken.uuid });
      return;
    }
    const uuid = crypto.randomUUID();
    edit({
      ...doc,
      doors: [...doc.doors, { uuid, segment: cell, kind: "door" }],
    });
    setSelection({ kind: "door", uuid });
  };
  const conflict =
    save.error instanceof ClinicAuthError && save.error.status === 409;

  /** Resolves to the problem when the map can't be saved. */
  const saveMap = async (): Promise<string | null> => {
    const problem = problemOf(doc);
    if (problem) return problem;
    const sent = doc;
    try {
      const result = await save.mutateAsync({
        expectedRevision: draft?.revision ?? saved.revision,
        document: withoutStaleOutside(sent),
      });
      // Edits made while the save was in flight stay, on top of the new revision.
      setDraft((current) =>
        current && current.doc !== sent
          ? { doc: current.doc, revision: result.revision }
          : null,
      );
      return null;
    } catch (error) {
      return describeMutationError(error, "save the map");
    }
  };

  const doorsSpace = doc.spaces.find((s) => s.uuid === doorsOf);
  const doorsRoom = doorsSpace?.room_uuid
    ? roomsById.get(doorsSpace.room_uuid)
    : undefined;

  return (
    <section className="flex min-w-0 flex-col gap-4 font-body">
      <header>
        <h2 className="font-display text-base font-medium text-ink">
          Clinic map
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-annotation">
          Draw the clinic once: each room and area, the doors between them and
          to the outside, and where each camera and screen sits. Brian uses it
          to know who is in the clinic and in each room.
        </p>
      </header>
      <div className="flex flex-wrap items-end gap-3">
        <SegmentedControl<MapTool>
          aria-label="Tool"
          size="sm"
          value={tool}
          options={TOOLS}
          onValueChange={(value) => {
            setTool(value);
            setAddBoxTo(null);
          }}
        />
        <Input
          label="Columns"
          type="number"
          min={1}
          max={GRID_MAX}
          className="w-20"
          value={doc.cols}
          onChange={(event) => {
            const cols = Number.parseInt(event.target.value, 10);
            if (cols >= 1 && cols <= GRID_MAX) edit({ ...doc, cols });
          }}
        />
        <Input
          label="Rows"
          type="number"
          min={1}
          max={GRID_MAX}
          className="w-20"
          value={doc.rows}
          onChange={(event) => {
            const rows = Number.parseInt(event.target.value, 10);
            if (rows >= 1 && rows <= GRID_MAX) edit({ ...doc, rows });
          }}
        />
        <div className="ml-auto flex items-center gap-2">
          <span className="font-mono text-xs text-annotation">
            {saved.revision === 0
              ? "Not saved yet"
              : `Version ${saved.revision}`}
            {draft && " · unsaved changes"}
            {draft && draft.revision !== saved.revision && (
              <span className="text-ink-attention">
                {" "}
                · someone saved a newer version
              </span>
            )}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={!draft || save.isPending}
            onClick={() => {
              setDraft(null);
              setSelection(null);
              save.reset();
            }}
          >
            Discard
          </Button>
          <Button
            size="sm"
            variant="accent"
            disabled={!draft}
            busy={save.isPending}
            onClick={() => void saveMap().then(setNotice)}
          >
            Save map
          </Button>
        </div>
      </div>
      {conflict && (
        <div className="flex items-center gap-2">
          <FieldError>Someone saved the map since you opened it.</FieldError>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDraft(null);
              save.reset();
              void map.refetch();
            }}
          >
            Load their map
          </Button>
        </div>
      )}
      {notice && !conflict && <FieldError>{notice}</FieldError>}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div
          tabIndex={0}
          className="min-w-0 rounded-paper border border-rule outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onKeyDown={(event) => {
            if (event.key !== "Delete" && event.key !== "Backspace") return;
            if (selection?.kind === "door") {
              edit(withoutDoor(doc, selection.uuid));
              setSelection(null);
            }
          }}
        >
          <MapCanvas
            doc={doc}
            rooms={roomsById}
            tool={tool}
            selection={selection}
            onSelect={setSelection}
            onChange={edit}
            addBoxTo={addBoxTo}
            onRefuse={setNotice}
            onWallCell={addDoor}
            displays={displays}
          />
        </div>
        <MapInspector
          doc={doc}
          onChange={edit}
          selection={selection}
          onSelect={setSelection}
          rooms={rooms.data}
          roomsById={roomsById}
          nodes={nodes.data ?? []}
          displays={displays}
          addBoxTo={addBoxTo}
          onAddBoxTo={(space) => {
            setAddBoxTo(space);
            if (space) setTool("space");
          }}
          onDrawDoors={(space: MapSpace) => setDoorsOf(space.uuid)}
          onDrawAreas={(space: MapSpace) =>
            setAreasRoomId(space.room_uuid ?? null)
          }
        />
      </div>

      {saved.warnings.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="font-mono text-mono-caps text-annotation">
            Worth fixing (as saved)
          </p>
          <ul className="flex flex-col gap-1">
            {saved.warnings.map((warning, index) => (
              <li key={index} className="text-sm text-ink-body">
                {warningText(warning, saved.document, roomsById, displays)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {areasRoomId && roomsById.get(areasRoomId)?.kind === "reception" && (
        <RoomAreasDialog
          room={roomsById.get(areasRoomId)!}
          doc={saved.document}
          rooms={roomsById}
          onClose={() => setAreasRoomId(null)}
        />
      )}
      {doorsSpace && doorsRoom && (
        <DoorAreasDialog
          space={doorsSpace}
          room={doorsRoom}
          doc={doc}
          rooms={roomsById}
          dirty={draft !== null}
          onDocChange={edit}
          onSaveMap={saveMap}
          onClose={() => setDoorsOf(null)}
        />
      )}
    </section>
  );
};
